import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../util/datetime';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { activeAdminTenantId, useAuth } from '../../auth/AuthContext';
import { extractErrorMessage } from '../../api/errors';
import { listParticipants, type Participant } from '../../api/tenants';
import { listProcedures, type Procedure } from '../../api/procedures';
import {
  listCaptureSessions,
  type CaptureSession,
} from '../../api/sessions';
import {
  listMasterBaselineProcedures,
  type MasterBaselineProcedure,
} from '../../api/masterBaselines';
import {
  MIN_COHORT_SESSIONS,
  getCohortFilterOptions,
  getCohortOverlap,
  listCohortSubmissions,
  previewCohortBaseline,
  submitCohortBaseline,
  type CohortFilterOptions,
  type CohortOverlap,
  type CohortStats,
  type CohortSubmission,
} from '../../api/cohortBaseline';
import { SearchableSelect } from '../../components/SearchableSelect';
import { SortHeader } from '../../components/SortHeader';
import { useSortedRows } from '../../util/tableSort';

/**
 * Tenant-admin "Cohort baseline" page (master-baseline-cohort-submit PR2).
 *
 * When a tenant has no IRCAD-style master data for a procedure, an admin
 * mints their own here: pick a procedure + cohort filter (participant job
 * title / years of experience / a single participant), preview each metric's
 * median / mean / p25 / p75 / N computed on the fly over their sessions, tick
 * the metrics to include, label the cohort, and submit for superadmin review.
 * Small cohorts are allowed — {@link MIN_COHORT_SESSIONS} is a recommendation
 * surfaced as a non-blocking warning, not a submit gate; the server only
 * refuses an empty cohort. The review is the quality gate.
 */

// The two cohorts a tenant-minted baseline can carry — the same
// practice_ready / not_practice_ready scheme the ACS + FlexVR reference
// sets use, so submissions speak the master-baseline vocabulary.
const COHORT_OPTIONS: { key: string; label: string }[] = [
  { key: 'practice_ready', label: 'Practice-ready' },
  { key: 'not_practice_ready', label: 'Not practice-ready' },
];

const ALL_PROCEDURES = [{ value: '', label: 'All procedures' }];
const ANY_TITLE = [{ value: '', label: 'Any title' }];
const ANY_YEARS = [{ value: '', label: 'Any' }];
const ANY_PARTICIPANT = [{ value: '', label: 'Any participant' }];
const ANY_RECORDING = [{ value: '', label: 'Any recording' }];
const NO_COHORT = [{ value: '', label: 'Select a cohort…' }];
const COHORT_SELECT_OPTIONS = COHORT_OPTIONS.map((c) => ({ value: c.key, label: c.label }));

type MetricRow = CohortStats['metrics'][number];
const METRIC_SORT = {
  metric: (m: MetricRow) => m.metric,
  modality: (m: MetricRow) => m.modality,
  median: (m: MetricRow) => m.median,
  mean: (m: MetricRow) => m.mean,
  p25: (m: MetricRow) => m.p25,
  p75: (m: MetricRow) => m.p75,
  n: (m: MetricRow) => m.n,
};
const SUBMISSION_SORT = {
  submitted: (s: CohortSubmission) => s.created_at,
  procedure: (s: CohortSubmission) => s.procedure_name ?? 'All',
  cohort: (s: CohortSubmission) => s.cohort,
  sessions: (s: CohortSubmission) => s.session_count,
  metrics: (s: CohortSubmission) => s.metrics.length,
  status: (s: CohortSubmission) => s.status,
};

function fmt(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (Number.isInteger(v)) return String(v);
  return Number(v.toFixed(3)).toString();
}

function statusColor(
  status: string,
): 'default' | 'success' | 'error' | 'warning' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'error';
  return 'warning';
}

export function CohortBaseline({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);

  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [sessions, setSessions] = useState<CaptureSession[]>([]);
  const [groups, setGroups] = useState<MasterBaselineProcedure[]>([]);
  const [options, setOptions] = useState<CohortFilterOptions>({
    titles: [],
    years: [],
  });
  const [submissions, setSubmissions] = useState<CohortSubmission[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Filter form.
  const [procedure, setProcedure] = useState('');
  const [baselineGroup, setBaselineGroup] = useState('');
  const [title, setTitle] = useState('');
  const [minYears, setMinYears] = useState('');
  const [maxYears, setMaxYears] = useState('');
  const [participantUserId, setParticipantUserId] = useState('');
  const [sessionId, setSessionId] = useState('');

  // Preview + selection.
  const [stats, setStats] = useState<CohortStats | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cohort, setCohort] = useState('');
  const [overlap, setOverlap] = useState<CohortOverlap | null>(null);

  // Submit.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);

  const loadSubmissions = useCallback(async () => {
    if (!tenantId) return;
    try {
      setSubmissions(await listCohortSubmissions(tenantId));
    } catch (err) {
      setLoadError(extractErrorMessage(err, 'failed to load submissions'));
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    listProcedures(tenantId)
      .then(setProcedures)
      .catch((err) =>
        setLoadError(extractErrorMessage(err, 'failed to load procedures')),
      );
    listParticipants(tenantId)
      .then(setParticipants)
      .catch(() => setParticipants([]));
    listCaptureSessions(tenantId)
      .then(setSessions)
      .catch(() => setSessions([]));
    listMasterBaselineProcedures()
      .then(setGroups)
      .catch(() => setGroups([]));
    void loadSubmissions();
  }, [tenantId, loadSubmissions]);

  // Distinct title / years present in the cohort universe (scoped to the
  // picked procedure) — powers the filter dropdowns.
  useEffect(() => {
    if (!tenantId) return;
    getCohortFilterOptions(tenantId, procedure || null)
      .then(setOptions)
      .catch(() => setOptions({ titles: [], years: [] }));
  }, [tenantId, procedure]);

  // Prefill the baseline group from the picked procedure's existing
  // mapping (if any). A specific procedure needs a group before submit;
  // "All procedures" is a groupless global baseline.
  const pickProcedure = (name: string) => {
    setProcedure(name);
    const proc = procedures.find((p) => p.name === name);
    setBaselineGroup(proc?.procedure_group ?? '');
  };

  // Group options: the catalogue, plus the procedure's existing group if
  // it's a legacy value not in the catalogue.
  const groupOptions = useMemo(() => {
    const opts = groups.map((g) => ({ key: g.key, label: g.display_name }));
    if (baselineGroup && !opts.some((o) => o.key === baselineGroup)) {
      opts.unshift({ key: baselineGroup, label: baselineGroup });
    }
    return opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }),
    );
  }, [groups, baselineGroup]);

  // Recordings the picker offers: real (non-deleted, participant-owned)
  // sessions, narrowed by the procedure / participant already chosen so the
  // list stays relevant, newest first.
  const sessionOptions = useMemo(() => {
    return sessions
      .filter((s) => s.participant_user_id !== null && s.deleted_at == null)
      .filter((s) => !procedure || s.procedure_name === procedure)
      .filter(
        (s) => !participantUserId || s.participant_user_id === participantUserId,
      )
      .sort((a, b) => (a.started_at < b.started_at ? 1 : -1));
  }, [sessions, procedure, participantUserId]);

  // If the chosen recording drops out of the narrowed list (procedure /
  // participant changed), clear it so the Select never shows a stale pick.
  useEffect(() => {
    if (sessionId && !sessionOptions.some((s) => s.session_id === sessionId)) {
      setSessionId('');
    }
  }, [sessionId, sessionOptions]);

  const sessionLabel = (s: CaptureSession): string => {
    const who = s.participant_label || s.participant_user_id?.slice(0, 8) || '—';
    const day = s.started_at ? s.started_at.slice(0, 10) : '';
    return `${s.procedure_name} · ${who}${day ? ` · ${day}` : ''}`;
  };

  const runPreview = useCallback(async () => {
    if (!tenantId) return;
    setPreviewing(true);
    setPreviewError(null);
    setSubmitOk(false);
    try {
      const res = await previewCohortBaseline(tenantId, {
        procedure: procedure || null,
        title: title || null,
        minYears: minYears === '' ? null : Number(minYears),
        maxYears: maxYears === '' ? null : Number(maxYears),
        participantUserId: participantUserId || null,
        sessionId: sessionId || null,
      });
      setStats(res);
      // Pre-select every metric that actually has data.
      setSelected(
        new Set(res.metrics.filter((m) => m.n > 0).map((m) => m.metric)),
      );
    } catch (err) {
      setStats(null);
      setPreviewError(extractErrorMessage(err, 'failed to compute preview'));
    } finally {
      setPreviewing(false);
    }
  }, [
    tenantId,
    procedure,
    title,
    minYears,
    maxYears,
    participantUserId,
    sessionId,
  ]);

  const toggle = (metric: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(metric)) next.delete(metric);
      else next.add(metric);
      return next;
    });
  };

  // Warn when the chosen cohort + metrics already have reference data
  // (they'd be skipped on approval). Debounced — cohort is free text.
  useEffect(() => {
    if (!tenantId || !stats || !cohort.trim() || selected.size === 0) {
      setOverlap(null);
      return;
    }
    const t = setTimeout(() => {
      void getCohortOverlap(tenantId, {
        cohort: cohort.trim(),
        procedure: procedure || null,
        baselineGroup: procedure ? baselineGroup : null,
        metrics: Array.from(selected),
      })
        .then(setOverlap)
        .catch(() => setOverlap(null));
    }, 400);
    return () => clearTimeout(t);
  }, [tenantId, stats, cohort, baselineGroup, procedure, selected]);

  // Small cohorts are allowed now (a single participant is a valid ask);
  // below the recommended size we warn but don't block. Only an empty
  // cohort can't be submitted.
  const smallSample =
    stats !== null &&
    stats.session_count > 0 &&
    stats.session_count < MIN_COHORT_SESSIONS;
  const needsGroup = procedure !== '' && baselineGroup.trim().length === 0;
  const canSubmit =
    !!stats &&
    stats.session_count > 0 &&
    selected.size > 0 &&
    cohort.trim().length > 0 &&
    !needsGroup &&
    !submitting;

  const handleSubmit = async () => {
    if (!tenantId || !stats || !canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(false);
    try {
      await submitCohortBaseline(tenantId, {
        procedure: procedure || null,
        title: title || null,
        min_years: minYears === '' ? null : Number(minYears),
        max_years: maxYears === '' ? null : Number(maxYears),
        participant_user_id: participantUserId || null,
        session_id: sessionId || null,
        cohort: cohort.trim(),
        metrics: Array.from(selected),
        baseline_group: procedure ? baselineGroup.trim() : null,
      });
      setSubmitOk(true);
      setStats(null);
      setSelected(new Set());
      setCohort('');
      setSessionId('');
      await loadSubmissions();
    } catch (err) {
      setSubmitError(extractErrorMessage(err, 'submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const metricRows = useMemo(() => stats?.metrics ?? [], [stats]);
  const { sort: metricSort, sorted: sortedMetrics } = useSortedRows(
    metricRows,
    METRIC_SORT,
    'metric',
  );
  // Newest first by default — the order the API returns them in.
  const { sort: subSort, sorted: sortedSubmissions } = useSortedRows(
    submissions,
    SUBMISSION_SORT,
    'submitted',
    'desc',
  );

  if (!tenantId) {
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. Switch to a
        tenant you administer to mint cohort baselines.
      </Alert>
    );
  }

  return (
    <Stack spacing={3} sx={embedded ? undefined : { maxWidth: 1100 }}>
      {!embedded && (
        <Stack spacing={0.5}>
          <Typography variant="h4">Cohort baseline</Typography>
          <Typography variant="body2" color="text.secondary">
            No master data for a procedure yet? Mint your own. Filter your
            captured sessions to a cohort — by procedure, job title, years of
            experience, or a single participant — preview each metric's median
            / mean / percentiles, then tag the metrics and submit them for
            platform review. Small cohorts are allowed; {MIN_COHORT_SESSIONS}+
            {' '}
            sessions make a more stable baseline, and the reviewer sees the
            sample size.
          </Typography>
        </Stack>
      )}

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {/* Filter form + preview, together as one card. */}
      <Paper>
        <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'flex-end' }}
          useFlexGap
          flexWrap="wrap"
        >
          <SearchableSelect
            label="Procedure"
            value={procedure}
            onChange={pickProcedure}
            pinned={ALL_PROCEDURES}
            options={procedures.map((p) => ({ value: p.name, label: p.name }))}
            sx={{ minWidth: 168 }}
          />
          <SearchableSelect
            label="Job title"
            value={title}
            onChange={setTitle}
            pinned={ANY_TITLE}
            options={options.titles.map((t) => ({ value: t, label: t }))}
            sx={{ minWidth: 140 }}
          />
          {/* Years stay in numeric order (keepOrder), not alphabetical. */}
          <SearchableSelect
            label="Min years"
            value={minYears}
            onChange={setMinYears}
            pinned={ANY_YEARS}
            options={options.years.map((y) => ({ value: String(y), label: String(y) }))}
            keepOrder
            sx={{ minWidth: 96 }}
          />
          <SearchableSelect
            label="Max years"
            value={maxYears}
            onChange={setMaxYears}
            pinned={ANY_YEARS}
            options={options.years.map((y) => ({ value: String(y), label: String(y) }))}
            keepOrder
            sx={{ minWidth: 96 }}
          />
          <SearchableSelect
            label="Participant"
            value={participantUserId}
            onChange={setParticipantUserId}
            pinned={ANY_PARTICIPANT}
            options={participants.map((p) => ({ value: p.id, label: p.full_name || p.email }))}
            sx={{ minWidth: 158 }}
          />
          {/* Recordings stay newest-first (keepOrder) — a timeline, not a name list. */}
          <SearchableSelect
            label="Recording"
            value={sessionId}
            onChange={setSessionId}
            pinned={ANY_RECORDING}
            options={sessionOptions.map((s) => ({ value: s.session_id, label: sessionLabel(s) }))}
            keepOrder
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            onClick={() => void runPreview()}
            disabled={previewing}
          >
            {previewing ? 'Previewing…' : 'Preview'}
          </Button>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1.5, display: 'block' }}
        >
          Pick one recording to baseline a single session.
        </Typography>
        </Box>

        {(previewError || submitOk) && (
          <Box sx={{ px: 2, pb: 2 }}>
            {previewError && <Alert severity="error">{previewError}</Alert>}
            {submitOk && (
              <Alert severity="success" sx={{ mt: previewError ? 1 : 0 }}>
                Submitted for review. Track its status below.
              </Alert>
            )}
          </Box>
        )}

        {/* Preview + selection — same card, below a divider. */}
        {stats && (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                label={`${stats.session_count} sessions`}
                color={smallSample ? 'warning' : 'default'}
                size="small"
              />
              <Chip
                label={`${stats.participant_count} participants`}
                size="small"
              />
            </Stack>

            {smallSample && (
              <Alert severity="warning">
                Small sample — this cohort has {stats.session_count} session(s).
                We recommend {MIN_COHORT_SESSIONS}+ for a stable baseline, but
                you can still submit; the reviewer sees the sample size.
              </Alert>
            )}
            {stats.session_count === 0 && (
              <Typography variant="body2" color="text.secondary">
                No sessions match this filter.
              </Typography>
            )}

            {stats.session_count > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <SortHeader label="Metric" col="metric" sort={metricSort} />
                      <SortHeader label="Modality" col="modality" sort={metricSort} />
                      <SortHeader label="Median" col="median" sort={metricSort} align="right" />
                      <SortHeader label="Mean" col="mean" sort={metricSort} align="right" />
                      <SortHeader label="p25" col="p25" sort={metricSort} align="right" />
                      <SortHeader label="p75" col="p75" sort={metricSort} align="right" />
                      <SortHeader label="N" col="n" sort={metricSort} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedMetrics.map((m) => {
                      const disabled = m.n === 0;
                      return (
                        <TableRow
                          key={m.metric}
                          hover
                          sx={disabled ? { opacity: 0.5 } : undefined}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={selected.has(m.metric)}
                              disabled={disabled}
                              onChange={() => toggle(m.metric)}
                              inputProps={{
                                'aria-label': `include ${m.metric}`,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {m.metric}
                            {m.unit ? (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 0.5 }}
                              >
                                ({m.unit})
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{m.modality}</TableCell>
                          <TableCell align="right">{fmt(m.median)}</TableCell>
                          <TableCell align="right">{fmt(m.mean)}</TableCell>
                          <TableCell align="right">{fmt(m.p25)}</TableCell>
                          <TableCell align="right">{fmt(m.p75)}</TableCell>
                          <TableCell align="right">{m.n}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {stats.session_count > 0 && overlap && overlap.would_skip > 0 && (
              <Alert severity="info">
                {overlap.would_skip} of the promoted rows already have
                reference data for cohort "{cohort.trim()}" and will be
                skipped on approval ({overlap.would_create} new).
              </Alert>
            )}

            {stats.session_count > 0 && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'flex-start' }}
              >
                <SearchableSelect
                  label="Cohort"
                  value={cohort}
                  onChange={setCohort}
                  pinned={NO_COHORT}
                  options={COHORT_SELECT_OPTIONS}
                  keepOrder
                  helperText="Which cohort this dataset represents"
                  sx={{ minWidth: 180 }}
                />
                {procedure !== '' && (
                  <Autocomplete
                    freeSolo
                    size="small"
                    sx={{ minWidth: 240 }}
                    options={groupOptions}
                    getOptionLabel={(o) =>
                      typeof o === 'string' ? o : o.label
                    }
                    // baselineGroup holds the group KEY; show the matching
                    // catalogue option (so its display_name renders) or, for a
                    // brand-new typed name, the raw string.
                    value={
                      groupOptions.find((o) => o.key === baselineGroup) ??
                      (baselineGroup || null)
                    }
                    onChange={(_e, val) => {
                      if (val && typeof val !== 'string') {
                        setBaselineGroup(val.key);
                      }
                    }}
                    onInputChange={(_e, input, reason) => {
                      // Ignore the programmatic reset that follows an option
                      // pick (onChange already set the key). Otherwise map a
                      // typed value to an existing group's key if it matches a
                      // label, else keep it as a new baseline name.
                      if (reason === 'reset') return;
                      const hit = groupOptions.find(
                        (o) =>
                          o.label.toLowerCase() ===
                          input.trim().toLowerCase(),
                      );
                      setBaselineGroup(hit ? hit.key : input.trim());
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Baseline group"
                        error={needsGroup}
                        helperText={
                          needsGroup
                            ? 'Required — pick an existing group or type a new name'
                            : 'Pick an existing group or type a new baseline name'
                        }
                      />
                    )}
                  />
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="contained"
                  onClick={() => void handleSubmit()}
                  disabled={!canSubmit}
                >
                  {submitting
                    ? 'Submitting…'
                    : `Submit ${selected.size} metric${
                        selected.size === 1 ? '' : 's'
                      } for review`}
                </Button>
              </Stack>
            )}
            {submitError && <Alert severity="error">{submitError}</Alert>}
          </Stack>
            </Box>
          </>
        )}

        {/* Submission history — same card, below a divider. */}
        <Divider />
        <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Your submissions
        </Typography>
        {submissions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No submissions yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortHeader label="Submitted" col="submitted" sort={subSort} />
                  <SortHeader label="Procedure" col="procedure" sort={subSort} />
                  <SortHeader label="Cohort" col="cohort" sort={subSort} />
                  <SortHeader label="Sessions" col="sessions" sort={subSort} align="right" />
                  <SortHeader label="Metrics" col="metrics" sort={subSort} align="right" />
                  <SortHeader label="Status" col="status" sort={subSort} />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSubmissions.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      {formatDate(s.created_at)}
                    </TableCell>
                    <TableCell>{s.procedure_name ?? 'All'}</TableCell>
                    <TableCell>{s.cohort}</TableCell>
                    <TableCell align="right">{s.session_count}</TableCell>
                    <TableCell align="right">{s.metrics.length}</TableCell>
                    <TableCell>
                      <Chip
                        label={s.status}
                        color={statusColor(s.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        </Box>
      </Paper>
    </Stack>
  );
}
